import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FactCard } from "@/components/FactCard";
import { EventCard } from "@/components/EventCard";
import { AdSlot } from "@/components/AdSlot";
import { useFavourites } from "@/context/FavouritesContext";
import { useAuth } from "@/context/AuthContext";

export default function Saved() {
  const [data, setData] = useState({ facts: [], events: [] });
  const [loading, setLoading] = useState(true);
  const { ids } = useFavourites();
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    api.get("/favourites").then((r) => setData(r.data)).catch(() => setData({ facts: [], events: [] })).finally(() => setLoading(false));
  }, [ids.size]);

  const empty = !loading && data.events.length === 0 && data.facts.length === 0;

  return (
    <div className="min-h-screen bg-paper-light">
      <Header />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 pb-6" data-testid="saved-page">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold mb-3">Your collection</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-ink flex items-center gap-3">
          <Bookmark className="w-9 h-9 text-gold fill-gold" /> Saved
        </h1>
        <p className="mt-3 text-base text-ink-muted">Every moment in history you've bookmarked, {user?.name ? user.name.split(" ")[0] : "kept"} in one curious place.</p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <AdSlot id="adsense-saved" slot="SAVED" size="leaderboard" className="mb-8" />
        {loading ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-28 rounded-xl bg-white border border-[#e5e7eb] animate-pulse" />)}</div>
        ) : empty ? (
          <div className="py-20 text-center bg-white border border-[#e5e7eb] rounded-2xl" data-testid="saved-empty">
            <Bookmark className="w-12 h-12 text-[#d1d5db] mx-auto mb-4" />
            <p className="text-2xl font-bold text-ink">Nothing saved yet.</p>
            <p className="mt-2 text-sm text-ink-muted">Tap the bookmark on any event to keep it here.</p>
            <Link to="/" data-testid="saved-browse" className="mt-6 inline-flex px-6 py-3 rounded-full bg-ink text-white font-semibold text-sm hover:bg-gold hover:text-ink transition-colors">Explore today in history</Link>
          </div>
        ) : (
          <>
            {data.events.length > 0 && (
              <div className="space-y-3" data-testid="saved-events">
                {data.events.map((e, i) => <EventCard key={e.id} e={e} i={i} total={data.events.length} showDate />)}
              </div>
            )}
            {data.facts.length > 0 && (
              <div className="mt-10">
                <h2 className="text-base md:text-lg font-bold text-ink mb-4">Saved facts</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="saved-facts">{data.facts.map((f, i) => <FactCard key={f.id} fact={f} index={i} />)}</div>
              </div>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
